// Tax document vault: kinds of documents, and guessing kind + financial year from a file name.

import { financialYear } from "./utils";

// Checked in this order when guessing from a file name (most specific first).
export const TAX_DOC_TYPES = [
  { value: "notice", label: "Notice / intimation / order", short: "Notice", tone: "bg-rose-50 text-rose-700 ring-rose-200", re: /notice|intimation|143\s*\(?1\)?|demand|order\b|rectification/i },
  { value: "computation", label: "Tax computation", short: "Computation", tone: "bg-violet-50 text-violet-700 ring-violet-200", re: /computation|comp(utation)?\s*of\s*income/i },
  { value: "itr", label: "ITR – income tax return / ITR-V", short: "ITR", tone: "bg-brand-50 text-brand-700 ring-brand-200", re: /\bitr|income\s*tax\s*return|acknowledge?ment/i },
  { value: "audit", label: "Tax audit report (3CA / 3CB / 3CD)", short: "Audit", tone: "bg-amber-50 text-amber-800 ring-amber-200", re: /audit|3c[abd]\b/i },
  { value: "ais", label: "Form 26AS / AIS / TIS", short: "26AS / AIS", tone: "bg-cyan-50 text-cyan-800 ring-cyan-200", re: /26\s*as|\bais\b|\btis\b|annual\s*information/i },
  { value: "tds", label: "TDS certificate (Form 16 / 16A)", short: "TDS cert.", tone: "bg-teal-50 text-teal-700 ring-teal-200", re: /form\s*-?\s*16|\b16a\b|tds\s*cert/i },
  { value: "gst", label: "GST return (GSTR-1 / 3B / 9)", short: "GST return", tone: "bg-emerald-50 text-emerald-700 ring-emerald-200", re: /gstr|\bgst\b/i },
  { value: "pnl", label: "Profit & loss account", short: "P&L", tone: "bg-sky-50 text-sky-700 ring-sky-200", re: /p\s*&\s*l|p\s*and\s*l|\bpnl\b|profit|loss|income\s*statement/i },
  { value: "balance", label: "Balance sheet", short: "Balance sheet", tone: "bg-indigo-50 text-indigo-700 ring-indigo-200", re: /balance\s*sheet|\bbs\b/i },
  { value: "challan", label: "Tax payment challan", short: "Challan", tone: "bg-orange-50 text-orange-800 ring-orange-200", re: /challan|itns|\b280\b|advance\s*tax|self\s*assessment/i },
  { value: "bank", label: "Bank statement", short: "Bank stmt.", tone: "bg-slate-100 text-slate-700 ring-slate-200", re: /bank|statement|passbook/i },
  { value: "other", label: "Other", short: "Other", tone: "bg-slate-100 text-slate-600 ring-slate-200", re: null },
];

export const taxDocType = (v) => TAX_DOC_TYPES.find((t) => t.value === v) || TAX_DOC_TYPES[TAX_DOC_TYPES.length - 1];

// What a year's folder should normally contain – shown as a checklist.
export const FY_CHECKLIST = ["itr", "computation", "pnl", "balance", "ais"];

export function guessTaxDocType(name) {
  const n = String(name || "").replace(/[_.-]+/g, " ");
  return (TAX_DOC_TYPES.find((t) => t.re && t.re.test(n)) || TAX_DOC_TYPES[TAX_DOC_TYPES.length - 1]).value;
}

const fy = (y) => `${y}-${String((y + 1) % 100).padStart(2, "0")}`;

// "ITR AY 2026-27.pdf" → "2025-26"; "P&L FY24-25" → "2024-25"; otherwise the last
// completed financial year (the one you'd normally be filing for).
export function guessFinancialYear(name, today = new Date().toISOString().slice(0, 10)) {
  const n = String(name || "");
  const pair = (re) => {
    const m = re.exec(n);
    if (!m) return null;
    let a = Number(m[1]);
    if (a < 100) a += 2000;
    const b = Number(m[2]);
    const bb = b < 100 ? 2000 + b : b;
    return bb === a + 1 ? a : null;
  };
  const ay = pair(/a\.?\s*y\.?\s*[-_ ]*(\d{2}|20\d{2})\s*[-–_/ ]\s*(\d{2}|20\d{2})(?!\d)/i);
  if (ay) return fy(ay - 1);
  const f = pair(/(?:f\.?\s*y\.?\s*[-_ ]*)?(?<!\d)(20\d{2}|\d{2})\s*[-–_/]\s*(\d{2}|20\d{2})(?!\d)/i);
  if (f && f >= 2000 && f < 2100) return fy(f);
  return previousFinancialYear(today);
}

export function previousFinancialYear(today = new Date().toISOString().slice(0, 10)) {
  const cur = Number(financialYear(today).slice(0, 4));
  return fy(cur - 1);
}

// Financial years to offer in pickers: current back to 2015-16.
export function financialYearOptions(today = new Date().toISOString().slice(0, 10)) {
  const cur = Number(financialYear(today).slice(0, 4));
  const out = [];
  for (let y = cur; y >= 2015; y--) out.push({ value: fy(y), label: `FY ${fy(y)} (AY ${fy(y + 1)})` });
  return out;
}

export const assessmentYear = (fyLabel) => {
  const y = Number(String(fyLabel).slice(0, 4));
  return y ? fy(y + 1) : "";
};

export function fileSize(n) {
  n = Number(n) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
